import { useState } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { employeeOnboardingGateway } from '../index';
import { EoInput, EoReadOnly, EoSelect } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import {
  formatPhoneForSave,
  isEmergencyBlank,
  validateEmergency,
  validatePersonal,
  type EmergencyErrors,
  type EmergencyForm,
  type PersonalErrors,
  type PersonalForm,
} from '../validation';
import type { SectionProps } from './sectionProps';

/** Gender is stored as free text today; these are the choices offered, plus whatever value is already saved. */
const GENDER_OPTIONS = ['Female', 'Male', 'Non-binary', 'Prefer not to say'];

export function PersonalSection({ setup, refresh, next, back }: SectionProps) {
  const p = setup.personal;
  const [form, setForm] = useState<PersonalForm>({
    firstName: p.firstName ?? '',
    lastName: p.lastName ?? '',
    phone: p.phone ?? '',
    gender: p.gender ?? '',
    dateOfBirth: p.dateOfBirth ?? '',
  });
  const [emergency, setEmergency] = useState<EmergencyForm>({
    fullName: setup.emergencyContact?.fullName ?? '',
    phone: setup.emergencyContact?.phone ?? '',
    relation: setup.emergencyContact?.relation ?? '',
  });
  const [errors, setErrors] = useState<PersonalErrors>({});
  const [emergencyErrors, setEmergencyErrors] = useState<EmergencyErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const genderOptions = form.gender && !GENDER_OPTIONS.includes(form.gender) ? [form.gender, ...GENDER_OPTIONS] : GENDER_OPTIONS;

  async function submit() {
    setError(null);
    const e = validatePersonal(form);
    const ee = validateEmergency(emergency);
    setErrors(e);
    setEmergencyErrors(ee);
    if (Object.keys(e).length > 0 || Object.keys(ee).length > 0) return;
    setBusy(true);
    try {
      await employeeOnboardingGateway.savePersonal({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: formatPhoneForSave(form.phone),
        gender: form.gender,
        dateOfBirth: form.dateOfBirth,
      });
      if (!isEmergencyBlank(emergency)) {
        await employeeOnboardingGateway.saveEmergencyContact({
          fullName: emergency.fullName.trim(),
          phone: formatPhoneForSave(emergency.phone),
          relation: emergency.relation.trim(),
        });
      }
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not save your personal information. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  function clearDraft() {
    // Only the employee's own editable fields. The email is read-only and is not touched.
    setForm({ firstName: '', lastName: '', phone: '', gender: '', dateOfBirth: '' });
    setEmergency({ fullName: '', phone: '', relation: '' });
    setErrors({});
    setEmergencyErrors({});
    setError(null);
  }

  return (
    <SectionFrame title="Personal Information" onSubmit={submit} onBack={back} onClearDraft={clearDraft} busy={busy} error={error}>
      <div className="eo-grid">
        <EoInput
          label="First Name"
          required
          value={form.firstName}
          autoComplete="given-name"
          maxLength={100}
          error={errors.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })}
        />
        <EoInput
          label="Last Name"
          required
          value={form.lastName}
          autoComplete="family-name"
          maxLength={100}
          error={errors.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })}
        />
        <EoInput
          label="Phone Number"
          required
          type="tel"
          value={form.phone}
          autoComplete="tel"
          placeholder="(555) 555-0123"
          error={errors.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <EoSelect label="Gender" required value={form.gender} error={errors.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
          <option value="">Select</option>
          {genderOptions.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </EoSelect>
        <EoInput
          label="Date of Birth"
          required
          type="date"
          value={form.dateOfBirth}
          autoComplete="bday"
          error={errors.dateOfBirth}
          onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
        />
        <EoReadOnly label="Email Address" value={p.email} />
      </div>

      <h3 className="eo-subtitle">Emergency Contact</h3>
      <div className="eo-grid">
        <EoInput
          label="Full Name"
          value={emergency.fullName}
          maxLength={150}
          error={emergencyErrors.fullName}
          onChange={(e) => setEmergency({ ...emergency, fullName: e.target.value })}
        />
        <EoInput
          label="Phone Number"
          type="tel"
          value={emergency.phone}
          placeholder="(555) 555-0123"
          error={emergencyErrors.phone}
          onChange={(e) => setEmergency({ ...emergency, phone: e.target.value })}
        />
        <EoInput
          label="Relation"
          value={emergency.relation}
          maxLength={100}
          error={emergencyErrors.relation}
          onChange={(e) => setEmergency({ ...emergency, relation: e.target.value })}
        />
      </div>
    </SectionFrame>
  );
}
